import * as vp from 'vopay';
import * as Yup from 'yup';
import { FunctionComponent, useEffect, useState } from 'react';
import { Formik } from 'formik';

import type { CreateUserHandler } from '../types';
import useBuildCreateUserRows from './buildCreateUserRows.helper';
import { useTranslation } from 'react-i18next';
import { usePartnerData } from '@/providers/PartnerData.provider';

const { Box, Button, Table, TextField, Select } = vp;

type CreateUserProps = {
  handlers: CreateUserHandler;
  defaultValues: vp.CreateUserRequest;
  reviewing: boolean;
};
type FC = FunctionComponent<CreateUserProps>;

const CreateUser: FC = (props: CreateUserProps) => {
  const { handlers, defaultValues, reviewing } = props;
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();
  const createUserRow = useBuildCreateUserRows();
  const [buttonIsDisabled, setButtonIsDisabled] = useState<boolean>(true);
  const { t } = useTranslation('translations', { keyPrefix: 'Users.Create' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  return (
    <Formik
      initialValues={defaultValues}
      onSubmit={reviewing ? handlers.onChangeCreateUser : handlers.onReview}
      validationSchema={createdValidationSchema()}
      validateOnChange
      validateOnBlur
    >
      {({ values, touched, errors, handleChange, handleBlur, handleSubmit }) => {
        useEffect(() => {
          const hasErrors = Object.entries(errors).length > 0;
          const filteredArr = Object.values(values).filter((value) => typeof value !== 'boolean');
          const allEmptyFields = Object.values(filteredArr).every((val) => !val);

          setButtonIsDisabled(allEmptyFields || hasErrors);
        }, [errors]);

        const createUserTable: vp.TableProps = {
          name: 'create-user-fields',
          variant: 'pair',
          columns: {
            Field: { header: tGlobal('Tables.labels') },
            Input: { header: tGlobal('Tables.fields') },
          },
          rows: createUserRow.map(({ label, id, preset, type, getSelectOptions, placeholder }) => ({
            Field: label,
            Input:
              type === 'text' ? (
                <TextField
                  preset={preset}
                  key={id}
                  name={id}
                  placeholder={placeholder}
                  variant="standard"
                  value={String(values[id])}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={errors[id] && touched[id] ? errors[id] : null}
                  disabled={reviewing}
                />
              ) : (
                <Select
                  variant="standard"
                  options={getSelectOptions()}
                  key={id}
                  name={id}
                  value={String(values[id])}
                  placeholder={placeholder}
                  noOrdering
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={errors[id] && touched[id] ? errors[id] : null}
                  disabled={reviewing}
                />
              ),
          })),
        };
        return (
          <form onSubmit={handleSubmit}>
            <Table {...createUserTable} />
            <Box mt="md" jc="fe">
              <Button
                id="CreateLead-submit"
                type="submit"
                label={reviewing ? t('Review.submitButton') : t('submitButton')}
                isDisabled={buttonIsDisabled || isRoleViewOnly}
              />
            </Box>
          </form>
        );
      }}
    </Formik>
  );
  function createdValidationSchema() {
    return Yup.object().shape({
      EmailAddress: Yup.string()
        .test('email-validation', t('emailValidation'), (value) => value?.trim() !== '')
        .label(t('emailAddressLabel'))
        .required(),
      Role: Yup.string().label(t('roleLabel')).required(),
    });
  }
};

export default CreateUser;
